/**
 * ภาษาอังกฤษของเว็บ — English for a Thai-first app.
 *
 * The app has ~1,200 Thai strings written straight into 92 components, which is the right way to write a
 * Thai product and the wrong shape for i18n. Wrapping every one of them in `t('…')` would be a two
 * thousand line diff across almost every file in the client, would collide with anything else in flight,
 * and would have to be repeated by hand for each string the team adds afterwards.
 *
 * So the translation happens in ONE place: a hook on Preact's vnode creation that swaps a Thai string for
 * its English twin on the way to the DOM. The dictionary (strings-en.ts) is keyed by the Thai text
 * itself, so there are no invented keys to keep in sync, and a string that is not in the dictionary
 * simply stays Thai — which is exactly the staged scope we agreed: the pages a visitor needs first, with
 * the back office left alone until someone decides otherwise.
 *
 * The language is the URL, not a setting: /courts is Thai, /en/courts is English. One address per
 * language is what makes a page shareable, what lets Google keep both versions, and what the server needs
 * in order to render the right <head> for a crawler that runs no JavaScript.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- a vnode's children are genuinely any
import { options, type VNode } from 'preact';
import { EN } from './strings-en';

export type Lang = 'th' | 'en';

const PREFIX = '/en';
let current: Lang = 'th';

export const lang = (): Lang => current;
export const isEn = (): boolean => current === 'en';

/** `/en/courts` → `/courts`. The router, and every route pattern, only ever sees the Thai path. */
export const stripLang = (path: string): string => (path === PREFIX ? '/' : path.replace(/^\/en(?=\/)/, '') || '/');

/**
 * `/@handle` → `handle`, in either language. Null for anything else.
 *
 * Lives here rather than in the router because it is really a question about the address: preact-iso
 * cannot express a route segment that is '@' plus a param, so the catch-all matches this by hand, and
 * the path it is handed still carries its `/en` prefix. Matching the raw path is why the English site
 * answered "page not found" for every avatar in it.
 */
export const handleInPath = (path: string): string | null => {
  const m = /^\/@([^/]+)$/.exec(stripLang(path));
  return m ? decodeURIComponent(m[1]!) : null;
};

/** `/courts` → `/en/courts` when the page is English. */
export const withLang = (path: string, l: Lang = current): string => {
  if (l !== 'en' || !path.startsWith('/')) return path;
  const clean = stripLang(path);
  return clean === '/' ? PREFIX : PREFIX + clean;
};

/** The same page in the other language, query string and hash kept. */
export const otherLangHref = (): string => {
  const { pathname, search, hash } = window.location;
  const target: Lang = current === 'en' ? 'th' : 'en';
  return withLang(stripLang(pathname), target) + search + hash;
};

/**
 * Translate one string. Anything the dictionary does not know stays Thai, on purpose.
 *
 * Whitespace is kept but not matched on: JSX writes `<IcPlus /> เสนอสถานที่` as the two children
 * `[icon, ' เสนอสถานที่']`, and a leading space is not something a dictionary key should have to know.
 */
export const t = (s: string): string => {
  if (current !== 'en') return s;
  const hit = EN[s];
  if (hit !== undefined) return hit;
  const trimmed = s.trim();
  if (!trimmed || trimmed === s) return s;
  const inner = EN[trimmed];
  return inner === undefined ? s : s.slice(0, s.indexOf(trimmed[0])) + inner + s.slice(s.indexOf(trimmed[0]) + trimmed.length);
};

/** Links the app writes itself (never user content), so a bare path can be given the prefix safely. */
const INTERNAL = /^\/(?!en(?:\/|$)|api\/|media\/|brand\/|fonts\/|assets\/|activities\/|courts\/[^/]+\.(?:jpg|png|webp)$)/;

/*
 * Props whose value is read by a person. `label`, `sub`, `desc` and friends are this app's own component
 * props — translating at the component boundary and again at the DOM is harmless, because a string the
 * dictionary has already turned into English matches no Thai key the second time.
 */
const TEXT_PROPS = ['placeholder', 'title', 'aria-label', 'alt', 'label', 'sub', 'desc', 'caption', 'hint', 'empty', 'action', 'confirmLabel', 'cancelLabel'];

function translateChildren(children: any): any {
  if (typeof children === 'string') return t(children);
  if (Array.isArray(children)) {
    let changed = false;
    const out = children.map((c) => {
      if (typeof c !== 'string') return c;
      const s = t(c);
      if (s !== c) changed = true;
      return s;
    });
    return changed ? out : children;
  }
  return children;
}

let installed = false;

/**
 * Turns the hook on for this page load. Called once, before the first render, with the language the URL
 * asked for — after that nothing in the app has to know which language it is being read in.
 */
export function installI18n(pathname: string): Lang {
  current = pathname === PREFIX || pathname.startsWith(PREFIX + '/') ? 'en' : 'th';
  document.documentElement.lang = current === 'en' ? 'en' : 'th';
  if (installed) return current;
  installed = true;

  const prev = options.vnode;
  options.vnode = (vnode: VNode<Record<string, unknown>>) => {
    if (current === 'en' && vnode.props) {
      const props = vnode.props;
      if (props.children !== undefined && !props.dangerouslySetInnerHTML) {
        const next = translateChildren(props.children);
        if (next !== props.children) props.children = next;
      }
      for (const key of TEXT_PROPS) {
        const v = props[key];
        if (typeof v === 'string' && v) {
          const s = t(v);
          if (s !== v) props[key] = s;
        }
      }
    }
    prev?.(vnode);
  };

  /*
   * Every <a href="/…"> in the app is written for the Thai site. Rather than touch 197 of them — in files
   * this scope does not otherwise translate — the prefix is added on the way out, in the capture phase,
   * before preact-iso's own click handler reads the href. A person browsing in English therefore stays in
   * English wherever they click, including the screens still written in Thai.
   */
  addEventListener(
    'click',
    (e: MouseEvent) => {
      if (current !== 'en' || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const link = e.composedPath().find((el) => (el as HTMLElement)?.nodeName === 'A') as HTMLAnchorElement | undefined;
      if (!link || link.target || link.hasAttribute('download') || link.dataset.lang === 'switch') return;
      const href = link.getAttribute('href');
      if (!href || !INTERNAL.test(href)) return;
      link.setAttribute('href', withLang(href));
    },
    true,
  );

  return current;
}
